import {
    createTheme,
    darken,
    lighten,
    Palette,
    PaletteColor,
    PaletteOptions,
    SimplePaletteColorOptions,
    Theme,
} from "@mui/material";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";

export type SenecBasePalette = {
    [key in keyof typeof senecColors]: SimplePaletteColorOptions;
} & {
    senecPrimary: typeof senecPrimaryColors;
    senecSecondary: typeof senecSecondaryColors;
} & Palette;

export type SenecBasePaletteOptions = SenecBasePalette & PaletteOptions;

/**
 * @deprecated use colors directly via color name, e.g.: deepBlue.main
 */
export const senecPrimaryColors = {
    deepBlue: "#000099", // Tiefenblau
    impulseOrange: "#FF8F0F", // Impuls-Orange
    impulsOrange: "#ff8f0f", // Impuls-Orange
    black: "#000000", // Schwarz
    warmGrey: "#e5dfda", // Warmgrau
    white: "#FFFFFF", // Weiß
    nightDarkGrey: "#1C1C1C", // Nacht Dunkelgrau
    darkGrayPremium: "#3C3C3C", // Dunkelgrau Premium
    darkWarmGrey: "#707070", // Dunkel Warmgrau
    lightWarmGrey: "#F2F2F2", // Hell Warmgrau
    darkWhite: "#F9F9FA", // Dunkelweiß
};

/**
 * @deprecated use colors directly via color name, e.g.: deepBlue.main
 */
export const senecSecondaryColors = {
    limeGreen: "#84c041", // Lindgrün
    aquamarin: "#5be3d6", // Aquamarin
    azur: "#1195eb", // Azur
    azur30: "#66C2FF", // Azur 30
    azur20: "#8fd4ff", // Azur 20
    sunYellow: "#fdc83a", // Sonnengelb
    greyBeige: "#e2c39a", // Graubeige
    tarocco: "#fc6538", // Tarocco
    warmGrey: "#c3bcb6", // Warmgrau
    neutralGrey: "#767676", //Neutralgrau
    neutralGray90: "#424242",
    neutralGray80: "#5B5B5B",
    neutralGray70: "#767676",
    neutralGray60: "#909090",
    neutralGray50: "#AAAAAA",
    neutralGray40: "#BDBDBD",
    neutralGray20: "#DCDCDC",
    neutralGray10: "#D1D1D1",
    neutralGray05: "#F0F0F0",
    neutralGray00: "#F2F2F2",
    neutralGray000: "#F8F8F8",
    azur40: "#BAE5FF",
    azur50: "#1195EB",
    azur60: "#0278D4",
    azur80: "#00348F",
    azur90: "#00185D",
    aquamarine60: "#1DC6B2",
    limeGreen70: "#538327",
    limeGreen50: "#84C041",
    limeGreen20: "#D4F0B6",
    sunYellow20: "#FEECA7",
    sunYellow50: "#FFC83A",
    sunYellow70: "#CA8A04",
    tarocco50: "#FC6538",
    beige60: "#CBA677",
};

export const senecColors = {
    azur90: "#00185D",
    azur80: "#00348F",
    azur60: "#0278D4",
    azur50: "#1195EB",
    azur40: "#BAE5FF",
    azur20: "#8fd4ff",
    aquamarine00: "#E7FDFA",
    aquamarine60: "#1DC6B2",
    aquamarine80: "#077B6F",
    beige80: "#826139",
    beige60: "#CBA677",
    beige00: "#FFFAF5",
    black: "#000000",
    deepBlue: "#000099",
    green: "#84C041",
    impulseOrange: "#FF8F0F",
    lavender50: "#9B51E8",
    limeGreen90: "#23400D",
    limeGreen80: "#3B6315",
    limeGreen70: "#538327",
    limeGreen60: "#70A638",
    limeGreen50: "#84C041",
    limeGreen30: "#B6DC8E",
    limeGreen20: "#D4F0B6",
    limeGreen00: "#EEF9E5",
    neutralGray110: "#1C1C1C",
    neutralGray100: "#2C2C2C",
    neutralGray90: "#424242",
    neutralGray80: "#5B5B5B",
    neutralGray70: "#767676",
    neutralGray60: "#909090",
    neutralGray50: "#AAAAAA",
    neutralGray40: "#BDBDBD",
    neutralGray30: "#D1D1D1",
    neutralGray20: "#DCDCDC",
    neutralGray10: "#E7E7E7",
    neutralGray10Transparency70: "#e7e7e7b3",
    neutralGray05: "#F0F0F0",
    neutralGray00: "#F2F2F2",
    neutralGray000: "#F8F8F8",
    red: "#E20E00",
    sunYellow90: "#7E4900",
    sunYellow80: "#9F6704",
    sunYellow70: "#CA8A04",
    sunYellow50: "#EDAF00",
    sunYellow60: "#FFC83A",
    sunYellow30: "#FEDE82",
    sunYellow20: "#FEECA7",
    sunYellow00: "#FCF8E5",
    surfaceBrandSecondary1Muted: "#D9F1FF",
    tarocco90: "#742209",
    tarocco80: "#A32D0B",
    tarocco60: "#E2491B",
    tarocco50: "#FC6538",
    tarocco30: "#FD9E83",
    tarocco00: "#FDF3F2",
    white: "#FFFFFF",
    yellow: "#FFBB00",
};

const createMuiColor = (theme: Theme, mainColor: string) =>
    theme.palette.augmentColor({
        color: { main: mainColor, light: lighten(mainColor, 0.15), dark: darken(mainColor, 0.15) },
    });

const createSenecMuiColors = (theme: Theme, colors: { [key: string]: string }) => {
    const generatedColors: { [key: string]: PaletteColor } = {};
    for (const [key, value] of Object.entries(colors)) {
        generatedColors[key] = createMuiColor(theme, value);
    }

    return generatedColors;
};

export const createPaletteTheme = (theme: Theme): SenecBaseTheme => {
    return createTheme(theme, {
        palette: {
            primary: {
                ...createMuiColor(theme, senecColors.deepBlue),
            },
            secondary: {
                ...createMuiColor(theme, senecColors.impulseOrange),
            },
            success: {
                ...createMuiColor(theme, senecColors.green),
            },
            error: {
                ...createMuiColor(theme, senecColors.red),
            },
            info: {
                ...createMuiColor(theme, senecColors.yellow),
            },
            text: {
                primary: senecColors.neutralGray100,
                secondary: senecColors.neutralGray90,
                light: senecColors.white,
            },
            ...createSenecMuiColors(theme, senecColors),
            senecPrimary: senecPrimaryColors,
            senecSecondary: senecSecondaryColors,
        },
    }) as SenecBaseTheme;
};
